import axios from "axios";
import { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaEdit,
  FaPlus,
  FaSearch,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import Back from "../../components/button/back";

function KelolaBerita() {
  const [berita, setBerita] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 6;
  const navigate = useNavigate();

  // =============================
  // FETCH DATA
  // =============================
  useEffect(() => {
    fetchBerita();
  }, []);

  const fetchBerita = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}api/berita`);
      setBerita(res.data);
    } catch (err) {
      console.error("Gagal mengambil berita:", err);
    }
  };

  // =============================
  // ACTION
  // =============================
  const handleEditBerita = (id) => {
    navigate("/addberita", { state: { ID: id } });
  };

  const handleDeleteBerita = async (id) => {
    if (!window.confirm("Yakin hapus berita ini?")) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}api/berita/${id}`);
      alert("Berita berhasil dihapus");
      fetchBerita();
    } catch (err) {
      console.error(err);
      alert("Gagal menghapus berita");
    }
  };

  // =============================
  // RENDER MEDIA (ARRAY SAFE)
  // =============================
  const renderMedia = (item) => {
    // MEDIA UPLOAD
    if (item.media && item.media.length > 0) {
      const media = item.media[0];

      if (media.mimetype.startsWith("video")) {
        return (
          <video
            controls
            className="w-full h-48 object-cover"
            src={`${process.env.REACT_APP_API_URL}api/berita/media/${media.filename}`}
          />
        );
      }

      return (
        <img
          src={`${process.env.REACT_APP_API_URL}api/berita/media/${media.filename}`}
          alt={item.judul}
          className="w-full h-48 object-cover"
        />
      );
    }

    // LINK VIDEO
    if (item.link && item.link.length > 0) {
      const embed = getEmbedLink(item.link[0]);

      if (embed) {
        return (
          <iframe
            src={embed}
            className="w-full h-48"
            allowFullScreen
            title="video"
          />
        );
      }

      return (
        <a
          href={item.link[0]}
          target="_blank"
          rel="noreferrer"
          className="w-full h-48 flex items-center justify-center bg-gray-200 text-blue-600 font-semibold"
        >
          Buka Link
        </a>
      );
    }

    // FALLBACK
    return (
      <div className="w-full h-48 bg-gray-100 flex items-center justify-center text-gray-500 italic">
        Tidak ada media
      </div>
    );
  };

  // =============================
  // EMBED LINK
  // =============================
  const getEmbedLink = (url) => {
    if (!url) return "";

    // YouTube
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
      if (match) return `https://www.youtube.com/embed/${match[1]}`;
    }

    // Google Drive
    if (url.includes("drive.google.com")) {
      const match = url.match(/\/d\/(.+?)\//);
      if (match) return `https://drive.google.com/file/d/${match[1]}/preview`;
    }

    // Facebook
    if (url.includes("facebook.com")) {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
        url,
      )}`;
    }

    return "";
  };

  // =============================
  // SEARCH & PAGINATION
  // =============================
  const filteredBerita = berita.filter((b) =>
    b.judul.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredBerita.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredBerita.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // =============================
  // RENDER
  // =============================
  return (
    <div className="container mx-auto p-6">
      {/* HEADER */}
      <div className="mb-6 flex gap-2">
        <div className="flex items-center gap-2 w-full">
          <FaSearch className="text-gray-500" />
          <input
            type="text"
            placeholder="Cari berita..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border rounded-md"
          />
        </div>

        <button
          onClick={() => navigate("/addberita")}
          className="bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
        >
          <FaPlus /> Tambah
        </button>
      </div>

      {/* LIST */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentItems.map((item) => (
          <div
            key={item._id}
            className="border rounded-lg overflow-hidden shadow"
          >
            {renderMedia(item)}

            <div className="p-4">
              <p className="text-xs text-gray-500">
                {new Date(item.tanggal).toLocaleDateString("id-ID")}
              </p>
              <h3 className="font-bold text-lg">{item.judul}</h3>
              <p className="text-sm text-gray-700 mt-1">
                {item.deskripsi?.substring(0, 100)}...
              </p>

              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => handleEditBerita(item._id)}
                  className="bg-yellow-500 text-white px-3 py-1 rounded flex items-center gap-1"
                >
                  <FaEdit /> Edit
                </button>

                <button
                  onClick={() => handleDeleteBerita(item._id)}
                  className="bg-red-600 text-white px-3 py-1 rounded"
                >
                  Hapus
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}
      <div className="flex justify-center items-center gap-4 mt-6">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
          className="px-4 py-2 bg-red-500 text-white rounded disabled:bg-gray-300"
        >
          <FaArrowLeft />
        </button>

        <span>
          {currentPage} / {totalPages}
        </span>

        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => p + 1)}
          className="px-4 py-2 bg-red-500 text-white rounded disabled:bg-gray-300"
        >
          <FaArrowRight />
        </button>
      </div>

      {/* BACK */}
      <div className="flex justify-center mt-6">
        <Link to="/admin">
          <Back />
        </Link>
      </div>
    </div>
  );
}

export default KelolaBerita;
